import type { OrderStatus } from '@/services/ordersApi'
import { isDeliveryLikeMethod, isPickupLikeMethod } from './ordersUx'

/** Filtros de la lista de órdenes. `statuses` permite agrupar estados por pestaña. */
export interface OrdersFilterState {
  status: OrderStatus | 'all'
  statuses?: OrderStatus[]
  search: string
  channelInstanceId: string
  from: string
  to: string
}

export const DEFAULT_ORDER_FILTERS: OrdersFilterState = {
  status: 'all',
  search: '',
  channelInstanceId: '',
  from: '',
  to: '',
}

export type OrderStatusTabId = 'all' | 'pending' | 'active' | 'completed'

export interface OrderStatusTab {
  id: OrderStatusTabId
  label: string
  statuses?: readonly OrderStatus[]
}

export const ORDER_STATUS_TABS: readonly OrderStatusTab[] = [
  { id: 'all', label: 'Todas' },
  { id: 'pending', label: 'Pendientes', statuses: ['draft', 'pending_confirmation'] },
  { id: 'active', label: 'En proceso', statuses: ['confirmed', 'in_progress', 'ready'] },
  { id: 'completed', label: 'Completadas', statuses: ['completed'] },
]

export function resolveOrderStatusTab(filters: OrdersFilterState): OrderStatusTabId {
  if (filters.statuses?.length) {
    const match = ORDER_STATUS_TABS.find(
      (tab) =>
        tab.statuses?.length === filters.statuses?.length &&
        tab.statuses?.every((status) => filters.statuses?.includes(status))
    )
    if (match) return match.id
    return 'all'
  }

  if (filters.status === 'all') return 'all'
  const byStatus = ORDER_STATUS_TABS.find((tab) => tab.statuses?.includes(filters.status as OrderStatus))
  return byStatus?.id ?? 'all'
}

export function applyOrderStatusTab(
  filters: OrdersFilterState,
  tabId: OrderStatusTabId
): OrdersFilterState {
  const tab = ORDER_STATUS_TABS.find((entry) => entry.id === tabId)
  if (!tab?.statuses?.length) {
    return { ...filters, status: 'all', statuses: undefined }
  }
  return { ...filters, status: tab.statuses[0], statuses: [...tab.statuses] }
}

export function countActiveOrderFilters(filters: OrdersFilterState): number {
  let count = 0
  if (filters.status !== 'all' || filters.statuses?.length) count += 1
  if (filters.search.trim()) count += 1
  if (filters.channelInstanceId) count += 1
  if (filters.from) count += 1
  if (filters.to) count += 1
  return count
}

export function buildOrderFiltersFromSearchParams(searchParams: URLSearchParams): OrdersFilterState {
  const status = searchParams.get('status')
  const statuses = searchParams.get('statuses')
  if (statuses) {
    const parsed = statuses
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry): entry is OrderStatus =>
        ORDER_STATUS_TABS.some((tab) => tab.statuses?.includes(entry as OrderStatus))
      )
    if (parsed.length > 0) {
      return { ...DEFAULT_ORDER_FILTERS, status: parsed[0], statuses: parsed }
    }
  }
  if (status && ORDER_STATUS_TABS.some((tab) => tab.statuses?.includes(status as OrderStatus))) {
    return { ...DEFAULT_ORDER_FILTERS, status: status as OrderStatus }
  }
  return DEFAULT_ORDER_FILTERS
}

export type FulfillmentFilterId = 'all' | 'delivery' | 'pickup'

export interface FulfillmentFilterOption {
  id: FulfillmentFilterId
  label: string
}

export const FULFILLMENT_FILTERS: readonly FulfillmentFilterOption[] = [
  { id: 'all', label: 'Todos' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'pickup', label: 'Retiro' },
]

export function filterOrdersByFulfillment<T extends { fulfillmentMethod: string | null }>(
  orders: readonly T[],
  filter: FulfillmentFilterId,
  enabled: boolean
): readonly T[] {
  if (!enabled || filter === 'all') return orders
  if (filter === 'delivery') return orders.filter((order) => isDeliveryLikeMethod(order.fulfillmentMethod || ''))
  return orders.filter((order) => isPickupLikeMethod(order.fulfillmentMethod || ''))
}

/** Conteos de las pestañas a partir del resumen real del negocio (no de la página cargada). */
export function getOrderTabCounts(summary?: {
  total: number
  pending: { count: number }
  active: { count: number }
  completed?: { count: number }
} | null): Record<OrderStatusTabId, number | null> {
  if (!summary) return { all: null, pending: null, active: null, completed: null }
  return {
    all: summary.total,
    pending: summary.pending.count,
    active: summary.active.count,
    completed: summary.completed?.count ?? null,
  }
}
